import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Picture from 'components/picture';
import { Calories } from 'components/price-and-calories';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { useUIContext } from 'state/ui';

import { UpsellItemButton, UpsellItemIcon } from './styles';
import { IUpsellItemProps } from './types';

const UpsellItemPicture = styled(Picture)`
  width: 70px;
  height: 50px;
  margin-right: 8px;
`;

const UpsellItemInfo = styled.div`
  font-size: 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const UpsellItemName = styled.div`
  font-size: 15px;
`;

const UpsellItemDetails = styled.div`
  text-align: left;
`;

const UpsellItemPrice = styled.div`
  font-weight: bold;
  display: inline;
`;

const UpsellItemCalories = styled.div`
  color: #626262;
  padding-left: 5px;
  display: inline;
`;

const UpsellItem: React.FC<IUpsellItemProps> = ({ item, addToCard }) => {
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const { pricingFunction } = useMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const formatJoules = useFormatJoules();
  const itemCalories = item.nutrition?.calories;

  const price = useMemo(() => formatCurrencyForLocale(pricingFunction(item)), [
    item,
    pricingFunction,
    formatCurrencyForLocale,
  ]);

  const caloriesInJoules = formatJoules(itemCalories);

  const calories = useMemo(() => formatCalories(itemCalories, 'calories'), [
    formatCalories,
    itemCalories,
  ]);

  const euFormattedNutritionText = formatMessage(
    { id: 'euNutritionValues' },
    { calories, joules: caloriesInJoules }
  );

  const globalizedCalorieFormat = calories ? `${euFormattedNutritionText}` : '';
  const finalCalories = enableEUNutritionDisplay ? globalizedCalorieFormat : calories;

  return (
    <UpsellItemButton
      data-testid="upsell-add-to-order-btn"
      aria-label={formatMessage({ id: 'addItemLabel' }, { itemName: item.name?.locale })}
      onClick={() => {
        addToCard(item);
      }}
    >
      <UpsellItemInfo
        aria-label={formatMessage(
          { id: 'upsellItemLabel' },
          {
            itemName: item.name?.locale,
            calories: formatCalories(itemCalories),
            price,
          }
        )}
      >
        <UpsellItemPicture image={item.image} alt={item.name?.locale} />
        <UpsellItemDetails>
          <UpsellItemName id={`upsell-name-${item._id}`}>{item.name?.locale}</UpsellItemName>
          <UpsellItemPrice id={`upsell-price-${item._id}`}>
            <span data-testid={`upsell-price-text-${item._id}`}>{price}</span>
          </UpsellItemPrice>
          <Calories>
            {' '}
            |
            <UpsellItemCalories id={`upsell-calories-${item._id}`}>
              {finalCalories}
            </UpsellItemCalories>
          </Calories>
        </UpsellItemDetails>
      </UpsellItemInfo>
      <UpsellItemIcon>+</UpsellItemIcon>
    </UpsellItemButton>
  );
};

export default UpsellItem;
